/**
 * Уведомления нового бэка — `/v2/notifications*`, `/v2/notification-preferences`
 * и `/v2/push-subscriptions`.
 *
 * Не путать с legacy-группой `/notifications` (числовые id, своя модель): она в
 * спеке v2 осталась, но на деве отвечает 404 — её заменяет эта.
 *
 * Две особенности, проверенные на живом стенде 10.09.2026:
 *
 *  - `payload` в спеке описан как `[]integer` — так swaggo рисует
 *    `json.RawMessage`. В рантайме там произвольный JSON, и его ключи спекой не
 *    описаны, поэтому разбор терпимый: `notificationText()` пробует набор
 *    вероятных полей и всегда откатывается на подпись по `type`;
 *  - `PATCH /v2/notification-preferences` принимает ЛЮБУЮ строку в `channel`
 *    (проверено — `bogus_channel` сохранился с кодом 200), а `GET` отдаёт
 *    только явно выставленные строки, без дефолтов. Поэтому набор каналов
 *    задан константой на фронте, а матрица настроек достраивается здесь.
 */
import { http } from '@/shared/api';
import type { SchemaV2 } from '@/shared/api';

export type NotificationV2 =
  SchemaV2<'tasks_module_internal_notify.Notification'>;
export type NotificationPreferenceV2 =
  SchemaV2<'tasks_module_internal_notify.Preference'>;
export type NotificationTypeV2 =
  SchemaV2<'tasks_module_internal_notify.NotificationType'>;
export type PushSubscriptionRequestV2 =
  SchemaV2<'internal_notify_handler.registerPushSubscriptionRequest'>;

/* --------------------------------------------------------------- лента --- */

export type FetchNotificationsParams = {
  limit?: number;
  offset?: number;
};

/** Непрочитанные уведомления текущего пользователя. */
export async function fetchNotificationsV2(
  params: FetchNotificationsParams = {}
): Promise<NotificationV2[]> {
  const { data } = await http.get<{ result?: NotificationV2[] }>(
    '/v2/notifications',
    { params }
  );

  return Array.isArray(data?.result) ? data.result : [];
}

export async function markNotificationReadV2(id: string): Promise<void> {
  await http.post(`/v2/notifications/${encodeURIComponent(id)}/read`);
}

export async function markAllNotificationsReadV2(): Promise<void> {
  await http.post('/v2/notifications/read-all');
}

/* ------------------------------------------------------------ настройки --- */

export async function fetchNotificationPreferencesV2(): Promise<
  NotificationPreferenceV2[]
> {
  const { data } = await http.get<{ result?: NotificationPreferenceV2[] }>(
    '/v2/notification-preferences'
  );

  return Array.isArray(data?.result) ? data.result : [];
}

export async function updateNotificationPreferenceV2(payload: {
  /** Не `NotificationTypeV2`: enum спеки отстаёт, см. model/types. */
  eventType: string;
  channel: string;
  enabled: boolean;
}): Promise<void> {
  await http.patch('/v2/notification-preferences', payload);
}

/* ---------------------------------------------------------------- push --- */

export async function registerPushSubscriptionV2(
  payload: PushSubscriptionRequestV2
): Promise<string> {
  const { data } = await http.post<{ id?: string }>(
    '/v2/push-subscriptions',
    payload
  );

  return data?.id ?? '';
}

export async function deletePushSubscriptionV2(id: string): Promise<void> {
  await http.delete(`/v2/push-subscriptions/${encodeURIComponent(id)}`);
}

/** Публичная часть VAPID-ключа сервера — без неё браузер не выдаст подписку. */
export async function fetchVapidPublicKeyV2(): Promise<string> {
  const { data } = await http.get<{ publicKey?: string }>(
    '/v2/push-subscriptions/vapid-public-key'
  );

  return data?.publicKey ?? '';
}
